import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Farms from "@/pages/Farms";
import FarmDetail from "@/pages/FarmDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderSuccess from "@/pages/OrderSuccess";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import About from "@/pages/About";
import FAQ from "@/pages/FAQ";
import Delivery from "@/pages/Delivery";
// Farm partner public + portal
import ForFarms from "@/pages/ForFarms";
import FarmApply from "@/pages/FarmApply";
import FarmLogin from "@/pages/FarmLogin";
import PartnerFarms from "@/pages/PartnerFarms";
import PartnerFarmDetail from "@/pages/PartnerFarmDetail";
import FarmPortal from "@/pages/farm/FarmPortal";
import FarmDashboard from "@/pages/farm/FarmDashboard";
import FarmProducts from "@/pages/farm/FarmProducts";
import FarmAvailability from "@/pages/farm/FarmAvailability";
import FarmOrders from "@/pages/farm/FarmOrders";
import FarmPickups from "@/pages/farm/FarmPickups";
import FarmPayments from "@/pages/farm/FarmPayments";
import FarmProfile from "@/pages/farm/FarmProfile";
import FarmPayoutInfo from "@/pages/farm/FarmPayoutInfo";
import FarmDocuments from "@/pages/farm/FarmDocuments";
import FarmMessages from "@/pages/farm/FarmMessages";
import FarmSettings from "@/pages/farm/FarmSettings";
// Admin portal
import AdminPortal from "@/pages/admin/AdminPortal";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminApplications from "@/pages/admin/AdminApplications";
import AdminProductApprovals from "@/pages/admin/AdminProductApprovals";
import AdminFarms from "@/pages/admin/AdminFarms";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminCapacity from "@/pages/admin/AdminCapacity";
import AdminBackupSuppliers from "@/pages/admin/AdminBackupSuppliers";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminBuildBoxItems from "@/pages/admin/AdminBuildBoxItems";
import AdminRoutes from "@/pages/admin/AdminRoutes";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminUsers from "@/pages/admin/AdminUsers";
import BuildBox from "@/pages/BuildBox";

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-cream">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
  </div>
);

const homeFor = (role) => (role === "admin" ? "/admin" : role === "farm_partner" ? "/farm/dashboard" : "/dashboard");

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
};

const RoleRoute = ({ role, children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to={role === "admin" ? "/login" : "/farm/login"} replace />;
  if (user.role !== role) return <Navigate to={homeFor(user.role)} replace />;
  return children;
};

function AppShell() {
  const location = useLocation();

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  const path = location.pathname;
  const isPortal = /^\/admin(\/|$)/.test(path) ||
    /^\/farm\/(dashboard|products|availability|orders|pickups|payments|profile|payout-info|documents|messages|settings)/.test(path);

  return (
    <>
      {!isPortal && <Header />}
      <main className={isPortal ? "" : "min-h-screen"}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/product/:productId" element={<ProductDetail />} />
          <Route path="/farms" element={<Farms />} />
          <Route path="/farm/:farmId" element={<FarmDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/build-a-box" element={<BuildBox />} />
          <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
          <Route path="/order-success" element={<ProtectedRoute><OrderSuccess /></ProtectedRoute>} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/delivery" element={<Delivery />} />

          {/* Farm partner public */}
          <Route path="/for-farms" element={<ForFarms />} />
          <Route path="/farm/apply" element={<FarmApply />} />
          <Route path="/farm/login" element={<FarmLogin />} />
          <Route path="/partner-farms" element={<PartnerFarms />} />
          <Route path="/partner-farms/:farmId" element={<PartnerFarmDetail />} />

          {/* Farm partner portal */}
          <Route path="/farm" element={<RoleRoute role="farm_partner"><FarmPortal /></RoleRoute>}>
            <Route index element={<Navigate to="/farm/dashboard" replace />} />
            <Route path="dashboard" element={<FarmDashboard />} />
            <Route path="products" element={<FarmProducts />} />
            <Route path="availability" element={<FarmAvailability />} />
            <Route path="orders" element={<FarmOrders />} />
            <Route path="pickups" element={<FarmPickups />} />
            <Route path="payments" element={<FarmPayments />} />
            <Route path="profile" element={<FarmProfile />} />
            <Route path="payout-info" element={<FarmPayoutInfo />} />
            <Route path="documents" element={<FarmDocuments />} />
            <Route path="messages" element={<FarmMessages />} />
            <Route path="settings" element={<FarmSettings />} />
          </Route>

          {/* Admin portal */}
          <Route path="/admin" element={<RoleRoute role="admin"><AdminPortal /></RoleRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="applications" element={<AdminApplications />} />
            <Route path="product-approvals" element={<AdminProductApprovals />} />
            <Route path="farms" element={<AdminFarms />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="capacity" element={<AdminCapacity />} />
            <Route path="backup" element={<AdminBackupSuppliers />} />
            <Route path="pricing" element={<AdminPricing />} />
            <Route path="build-box-items" element={<AdminBuildBoxItems />} />
            <Route path="routes" element={<AdminRoutes />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="messages" element={<AdminMessages />} />
            <Route path="users" element={<AdminUsers />} />
          </Route>
        </Routes>
      </main>
      {!isPortal && <Footer />}
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <AppShell />
          <Toaster position="top-right" richColors />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
